
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';

function NavigateDialog(props) {
  const {
    i18n, dialogContentProps, disableNavigateDialog,
    disableNavigateDialogOnce, setDisableNavigateDialogOnce,
  } = props;
  const history = useReportsHistory();
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [navigateParam, setNavigateParam] = useState({});
  const unblockRef = useRef();

  function onCheckedNavigate() {
    unblockRef.current(); // Unblock immediately. Do not use setDisableNavigateDialogOnce(true) because it does not rerender immediately.
    setDialogVisible(false);
    history[navigateParam.action.toLowerCase()](navigateParam.location);
  }

  // Block in-page navigation
  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (disableNavigateDialogOnce) {
        setDisableNavigateDialogOnce(false);
        return true; // allow navigation
      }

      if (disableNavigateDialog) {
        return true; // allow navigation
      }

      // history package v4 not support retry so we cannot judge whether user is clicking back or forward button of browser. So only block PUSH action to avoid this bug
      // upgrade to v5 will fix this bug, and also support block on window.beforeUnload event
      if (action === 'POP') {
        return true; // allow navigation
      }

      setDialogVisible(true);
      setNavigateParam({ location, action });
      return false; // block navigation
    });

    unblockRef.current = unblock;

    return unblock;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableNavigateDialog, disableNavigateDialogOnce]);

  // Block page reload or tab close through clicking on browser's buttons
  useEffect(() => {
    function promptBeforeUnload(event) {
      if (disableNavigateDialog || disableNavigateDialogOnce) {
        return;
      }

      // Cancel the event.
      event.preventDefault();
      // Chrome (and legacy IE) requires returnValue to be set.
      event.returnValue = ''; // eslint-disable-line no-param-reassign
    }

    window.addEventListener('beforeunload', promptBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', promptBeforeUnload);
    };
  }, [disableNavigateDialog, disableNavigateDialogOnce]);

  return (
    <Dialog
      hidden={!isDialogVisible}
      onDismiss={() => setDialogVisible(false)}
      className="navigation-block-dialog"
      minWidth={474}
      dialogContentProps={dialogContentProps || {
        type: DialogType.normal,
        title: i18n.getString(_TL_('Navigation away confirmation')),
        subText: i18n.getString(_TL_('Are you sure you want to navigate away and lose your changes?')),
      }}
    >
      <DialogFooter>
        <PrimaryButton onClick={onCheckedNavigate} text={i18n.getString(_TL_('Yes'))} />
        <DefaultButton onClick={() => setDialogVisible(false)} text={i18n.getString(_TL_('Cancel'))} />
      </DialogFooter>
    </Dialog>
  );
}

NavigateDialog.defaultProps = {
  dialogContentProps: undefined,
  disableNavigateDialog: false,
  disableNavigateDialogOnce: false,
  setDisableNavigateDialogOnce: () => { },
};

NavigateDialog.propTypes = {
  history: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  dialogContentProps: PropTypes.shape({}),
  disableNavigateDialog: PropTypes.bool,
  disableNavigateDialogOnce: PropTypes.bool,
  setDisableNavigateDialogOnce: PropTypes.func,
};

export { NavigateDialog };
